import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Mail, Clock, Send, MessageSquare } from 'lucide-react';

export const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        toast.success(t('contact.success'));
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || t('contact.error'));
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('contact.connection_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-14 sm:py-20 lg:py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, hsl(258 26% 8%) 0%, hsl(262 28% 11%) 50%, hsl(258 26% 8%) 100%)' }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 right-1/3 w-80 h-80 rounded-full bg-[hsl(var(--primary))] opacity-[0.07] blur-[100px]" />
        <div className="absolute bottom-0 left-1/4 w-72 h-72 rounded-full bg-[hsl(var(--neon-accent))] opacity-[0.05] blur-[80px]" />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <Badge variant="outline" className="mb-4 border-[hsl(var(--primary-glow)/0.5)] text-[hsl(var(--primary-glow))] bg-[hsl(var(--primary)/0.15)] shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
              <MessageSquare className="w-3 h-3 mr-1.5" /> {t('contact.badge')}
            </Badge>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">
              <span className="text-gradient text-neon drop-shadow-[0_0_30px_hsl(var(--primary-glow)/0.6)]">{t('contact.title')}</span>
            </h2>
            <p className="text-base text-white/80 leading-relaxed mb-8">{t('contact.subtitle')}</p>
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary)/0.2)] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_hsl(var(--primary)/0.2)]">
                  <Mail className="w-6 h-6 text-[hsl(var(--primary-glow))] drop-shadow-[0_0_6px_hsl(var(--primary-glow))]" />
                </div>
                <div>
                  <div className="font-bold text-white text-sm mb-0.5">{t('contact.email')}</div>
                  <a href="mailto:hola@fluxipay.com" className="text-sm text-[hsl(var(--primary-glow))] hover:text-[hsl(var(--neon-accent))] transition-colors duration-200 drop-shadow-[0_0_4px_hsl(var(--primary-glow)/0.5)]">hola@fluxipay.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--success)/0.2)] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_hsl(var(--success)/0.2)]">
                  <Clock className="w-6 h-6 text-[hsl(var(--success))] drop-shadow-[0_0_6px_hsl(var(--success))]" />
                </div>
                <div>
                  <div className="font-bold text-white text-sm mb-0.5">{t('contact.schedule')}</div>
                  <div className="text-sm text-white/75">{t('contact.schedule_hours')}</div>
                </div>
              </div>
            </div>
          </div>
          <Card className="p-6 sm:p-8 bg-[hsl(var(--surface)/0.9)] border-[hsl(var(--primary-glow)/0.35)] shadow-[0_0_30px_hsl(var(--primary)/0.15)] backdrop-blur-md relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary-glow)/0.7)] to-transparent" />
            <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-4 relative z-10">
              <div>
                <Label htmlFor="name" className="text-white/80 text-sm font-semibold">{t('contact.label_name')}</Label>
                <Input id="name" name="name" data-testid="contact-input-name" type="text" required value={formData.name} onChange={handleChange} disabled={loading}
                  className="mt-1.5 bg-[hsl(var(--background))] border-[hsl(var(--border)/0.5)] text-white focus:border-[hsl(var(--primary-glow)/0.6)] focus:ring-[hsl(var(--primary-glow)/0.2)] focus:shadow-[0_0_12px_hsl(var(--primary-glow)/0.2)]" placeholder={t('contact.placeholder_name')} />
              </div>
              <div>
                <Label htmlFor="email" className="text-white/80 text-sm font-semibold">{t('contact.label_email')}</Label>
                <Input id="email" name="email" data-testid="contact-input-email" type="email" required value={formData.email} onChange={handleChange} disabled={loading}
                  className="mt-1.5 bg-[hsl(var(--background))] border-[hsl(var(--border)/0.5)] text-white focus:border-[hsl(var(--primary-glow)/0.6)] focus:ring-[hsl(var(--primary-glow)/0.2)] focus:shadow-[0_0_12px_hsl(var(--primary-glow)/0.2)]" placeholder={t('contact.placeholder_email')} />
              </div>
              <div>
                <Label htmlFor="company" className="text-white/80 text-sm font-semibold">{t('contact.label_company')}</Label>
                <Input id="company" name="company" data-testid="contact-input-company" type="text" value={formData.company} onChange={handleChange} disabled={loading}
                  className="mt-1.5 bg-[hsl(var(--background))] border-[hsl(var(--border)/0.5)] text-white focus:border-[hsl(var(--primary-glow)/0.6)] focus:ring-[hsl(var(--primary-glow)/0.2)] focus:shadow-[0_0_12px_hsl(var(--primary-glow)/0.2)]" placeholder={t('contact.placeholder_company')} />
              </div>
              <div>
                <Label htmlFor="message" className="text-white/80 text-sm font-semibold">{t('contact.label_message')}</Label>
                <Textarea id="message" name="message" data-testid="contact-input-message" required value={formData.message} onChange={handleChange} disabled={loading} rows={4}
                  className="mt-1.5 bg-[hsl(var(--background))] border-[hsl(var(--border)/0.5)] text-white focus:border-[hsl(var(--primary-glow)/0.6)] focus:ring-[hsl(var(--primary-glow)/0.2)] focus:shadow-[0_0_12px_hsl(var(--primary-glow)/0.2)] resize-none" placeholder={t('contact.placeholder_message')} />
              </div>
              <Button data-testid="contact-submit-button" type="submit" className="w-full btn-neon h-12 gap-2 group text-base font-semibold shadow-[0_0_25px_hsl(var(--primary)/0.4)]" disabled={loading}>
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('contact.sending')}
                  </span>
                ) : (
                  <>
                    {t('contact.submit')}
                    <Send className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
